import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import type {
  IHotelWithDetails,
  IMergeRoomItem,
} from 'app/typings/migrationTool/HotelWithDetails';

import type { MergeSelection } from '../../HotelMigration';
import { getRoomWithBedsList } from '../../utils';
import { MigrationLayout } from '../MigrationLayout';

import './Rooms.scss';

type RoomsProps = {
  hotels?: IHotelWithDetails[];
  parentHotelId: number;
  childHotelId?: number;
  selections: Record<number, MergeSelection>;
  setSelections: (newSelections: Record<number, MergeSelection>) => void;
};

export const Rooms = ({
  hotels,
  parentHotelId,
  childHotelId,
  selections,
  setSelections,
}: RoomsProps) => {
  const { data: bedCategories } = useBedCategories();
  const { currencyFormatter } = useContext(CurrencyContext);

  if (!hotels || !childHotelId) {
    return null;
  }

  const parentHotel = hotels.find((h) => h.hotel.id === parentHotelId);
  const childHotel = hotels.find((h) => h.hotel.id === childHotelId);

  if (!parentHotel || !childHotel) {
    return null;
  }

  const actionList = parentHotel.rooms.map((room) => ({
    label: room.name,
    isPublished: room.isPublished,
    value: room.id,
    disabled: Object.values(selections).some(
      (selection) => selection.targetId === room.id
    ),
  }));

  const roomsWithBedsList = childHotel.rooms.map((room) => {
    return getRoomWithBedsList(room, currencyFormatter, bedCategories);
  });

  return (
    <MigrationLayout<IMergeRoomItem & { bedsList?: string[] }>
      title="Merge rooms from your child hotel with your parent hotel"
      description="You can decide to merge an existing room, not migrate one, or create a new room in the parent hotel."
      childHotel={childHotel.hotel.name}
      hotel={parentHotel}
      actionList={actionList}
      items={roomsWithBedsList}
      getItemId={(room) => room.id}
      entityLabel="Room"
      selections={selections}
      setSelections={setSelections}
    />
  );
};
